import React, { useEffect, useRef, useState } from "react";

import {
  applyPan,
  applyZoom,
  CARD_HEIGHT,
  CARD_WIDTH,
  hitTest,
  type HitTarget,
  type ImageSizes,
  type PhotoCompareSlotKey,
  type PhotoCompareState,
  type Point,
} from "@fitness-app/client-shared/features/measurements/photo-compare-layout";

import { drawPhotoCompare, type PhotoCompareImages, type PhotoCompareTexts } from "./drawPhotoCompare";

// TRE-2290 — interactive preview of the comparison card, ported from client-web
// (photo-compare-canvas.tsx). Pointer events are converted into card units and handed to the
// shared layout helpers: one pointer drags (divider / photo), two pointers pinch, the wheel zooms
// the photo under the cursor.

type StateUpdater = (update: (state: PhotoCompareState) => PhotoCompareState) => void;

interface PhotoCompareCanvasProps {
  images: PhotoCompareImages;
  texts: PhotoCompareTexts;
  state: PhotoCompareState;
  onStateChange: StateUpdater;
  /** Fires when a pointer lands on a photo, so the modal knows which photo the zoom buttons target. */
  onSlotTouched?: (slot: PhotoCompareSlotKey) => void;
  /** Rendered CSS width; the height follows the card's aspect ratio. */
  width: number;
  ariaLabel: string;
}

type DragSession = { target: HitTarget; last: Point };
type PinchSession = { target: HitTarget; lastDistance: number };
type Cursor = "grab" | "grabbing" | "ew-resize" | "default";

export const imageSizes = (images: PhotoCompareImages): ImageSizes => ({
  before: images.before ? { width: images.before.naturalWidth, height: images.before.naturalHeight } : null,
  after: images.after ? { width: images.after.naturalWidth, height: images.after.naturalHeight } : null,
});

const cursorFor = (target: HitTarget, hover = false): Cursor => {
  if (!target) {
    return "default";
  }
  if (target.kind === "divider") {
    return "ew-resize";
  }
  return hover ? "grab" : "grabbing";
};

const distance = (a: Point, b: Point) => Math.hypot(a.x - b.x, a.y - b.y);
const midpoint = (a: Point, b: Point): Point => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });

// Fingers on either side of the slider put the midpoint on the divider, which cannot zoom, so the
// pinch goes to the photo under the first finger instead.
const pinchTarget = (state: PhotoCompareState, sizes: ImageSizes, a: Point, b: Point): HitTarget => {
  const target = hitTest(state, sizes, midpoint(a, b));
  if (target?.kind !== "divider") {
    return target;
  }
  const first = hitTest(state, sizes, a);
  if (first?.kind === "photo") {
    return first;
  }
  return { kind: "photo", slot: a.x < state.split * CARD_WIDTH ? "before" : "after" };
};

export const PhotoCompareCanvas = ({
  images,
  texts,
  state,
  onStateChange,
  onSlotTouched,
  width,
  ariaLabel,
}: PhotoCompareCanvasProps): React.ReactElement => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const height = (width * CARD_HEIGHT) / CARD_WIDTH;
  const [cursor, setCursor] = useState<Cursor>("default");

  const stateRef = useRef(state);
  stateRef.current = state;
  const sizesRef = useRef(imageSizes(images));
  sizesRef.current = imageSizes(images);

  const pointers = useRef(new Map<number, Point>());
  const drag = useRef<DragSession | null>(null);
  const pinch = useRef<PinchSession | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) {
      return;
    }
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale((width * dpr) / CARD_WIDTH, (height * dpr) / CARD_HEIGHT);
    drawPhotoCompare(ctx, images, state, texts);
  }, [images, state, texts, width, height]);

  const toCard = (clientX: number, clientY: number): Point => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) {
      return { x: 0, y: 0 };
    }
    return {
      x: ((clientX - rect.left) / rect.width) * CARD_WIDTH,
      y: ((clientY - rect.top) / rect.height) * CARD_HEIGHT,
    };
  };

  const onPointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    const point = toCard(event.clientX, event.clientY);
    pointers.current.set(event.pointerId, point);

    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()] as [Point, Point];
      drag.current = null;
      const target = pinchTarget(stateRef.current, sizesRef.current, a, b);
      pinch.current = { target, lastDistance: distance(a, b) };
      if (target && target.kind !== "divider") {
        onSlotTouched?.(target.slot);
      }
      return;
    }
    const target = hitTest(stateRef.current, sizesRef.current, point);
    drag.current = { target, last: point };
    setCursor(cursorFor(target));
    if (target && target.kind !== "divider") {
      onSlotTouched?.(target.slot);
    }
  };

  const onPointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const point = toCard(event.clientX, event.clientY);
    if (!pointers.current.has(event.pointerId)) {
      setCursor(cursorFor(hitTest(stateRef.current, sizesRef.current, point), true));
      return;
    }
    pointers.current.set(event.pointerId, point);

    if (pinch.current && pointers.current.size >= 2) {
      const [a, b] = [...pointers.current.values()] as [Point, Point];
      const now = distance(a, b);
      const session = pinch.current;
      if (session.lastDistance > 0) {
        const factor = now / session.lastDistance;
        onStateChange((current) => applyZoom(current, sizesRef.current, session.target, factor));
      }
      session.lastDistance = now;
      return;
    }

    const session = drag.current;
    if (!session) {
      return;
    }
    const delta = { x: point.x - session.last.x, y: point.y - session.last.y };
    session.last = point;
    onStateChange((current) => applyPan(current, sizesRef.current, session.target, delta, point));
  };

  const onPointerEnd = (event: React.PointerEvent<HTMLCanvasElement>) => {
    pointers.current.delete(event.pointerId);
    if (pointers.current.size < 2) {
      pinch.current = null;
    }
    if (pointers.current.size === 0) {
      drag.current = null;
      setCursor("default");
    } else if (pointers.current.size === 1) {
      // Lifting one finger of a pinch continues as a drag with the remaining one.
      const [remaining] = [...pointers.current.values()] as [Point];
      drag.current = { target: hitTest(stateRef.current, sizesRef.current, remaining), last: remaining };
    }
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    // Wheel must be non-passive to stop the modal from scrolling while zooming a photo.
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const point = {
        x: ((event.clientX - rect.left) / rect.width) * CARD_WIDTH,
        y: ((event.clientY - rect.top) / rect.height) * CARD_HEIGHT,
      };
      const target = hitTest(stateRef.current, sizesRef.current, point);
      const factor = Math.exp(-event.deltaY * 0.002);
      onStateChange((current) => applyZoom(current, sizesRef.current, target, factor));
    };
    canvas.addEventListener("wheel", onWheel, { passive: false });
    return () => canvas.removeEventListener("wheel", onWheel);
  }, [onStateChange]);

  return (
    <canvas
      ref={canvasRef}
      role="img"
      aria-label={ariaLabel}
      className="ph-no-capture"
      style={{ width, height, borderRadius: 12, touchAction: "none", cursor, display: "block" }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerEnd}
      onPointerCancel={onPointerEnd}
      onPointerLeave={(event) => {
        if (!pointers.current.has(event.pointerId)) {
          setCursor("default");
        }
      }}
    />
  );
};
